// ui/src/hooks/useModuleAccess.ts
//
// One place the UI asks "can I use this module?".
//
// Reads the three lists returned by /adriva_api/auth/me and /auth/login:
//
//   modules             effective = deployed AND entitled  -> launchable
//   deployment_modules  what this environment serves (ENABLED_MODULES)
//   entitled_modules    what this user is entitled to (access_config.json)
//
// Three states, because there are two independent server-side gates:
//
//   'available'      render the tile, enabled
//   'not-entitled'   render the tile, disabled, offer "Request access"
//                    (the module IS installed here; someone else can use it)
//   'not-deployed'   do not render at all
//                    (not installed here; nobody can use it, so a permanently
//                     greyed tile is just noise)

import { useMemo } from "react";
import { useAuth } from "./useAuth";

export type ModuleCode = "DQ" | "MDM" | "DG";
export type ModuleState = "available" | "not-entitled" | "not-deployed";

const ALL: ModuleCode[] = ["DQ", "MDM", "DG"];

export interface ModuleAccess {
  stateOf: (m: ModuleCode) => ModuleState;
  canUse: (m: ModuleCode) => boolean;
  /** Modules to render at all — excludes anything not deployed here. */
  visible: ModuleCode[];
  /** Modules that can actually be launched. */
  usable: ModuleCode[];
  /** True when this environment serves exactly one module. */
  isSingleModule: boolean;
}

export function useModuleAccess(): ModuleAccess {
  const { user } = useAuth();

  return useMemo<ModuleAccess>(() => {
    const ent = user?.entitlements;

    // Effective list from the server (already intersected server-side).
    const effective = (ent?.modules ?? []) as ModuleCode[];

    // Backwards compatible: on an older server the two new fields are absent,
    // so fall back to the previous behaviour rather than hiding everything.
    const deployed: ModuleCode[] =
      (ent?.deployment_modules as ModuleCode[] | undefined) ??
      (effective.length ? effective : ALL);

    const entitled: ModuleCode[] =
      (ent?.entitled_modules as ModuleCode[] | undefined) ??
      (effective.length ? effective : (user?.is_admin ? ALL : (["DG"] as ModuleCode[])));

    const stateOf = (m: ModuleCode): ModuleState => {
      if (!deployed.includes(m)) return "not-deployed";
      if (!entitled.includes(m)) return "not-entitled";
      return "available";
    };

    const visible = ALL.filter((m) => stateOf(m) !== "not-deployed");
    const usable = ALL.filter((m) => stateOf(m) === "available");

    return {
      stateOf,
      canUse: (m: ModuleCode) => stateOf(m) === "available",
      visible,
      usable,
      isSingleModule: visible.length === 1,
    };
  }, [user]);
}